/* eslint-disable no-console */
/**
 * LLM Logger Utility
 * Provides consistent logging for all LLM API calls for debugging
 */

const LOG_ENABLED =
  process.env.REACT_APP_LLM_DEBUG === 'true' || process.env.NODE_ENV === 'development';

/**
 * Log LLM request
 * @param {string} serviceName - Name of the service
 * @param {string} methodName - Name of the method
 * @param {string} endpoint - API endpoint
 * @param {Object} payload - Request payload
 */
export const logRequest = (serviceName, methodName, endpoint, payload) => {
  if (!LOG_ENABLED) return;

  console.group(`🤖 [LLM Request] ${serviceName}.${methodName}`);
  console.log('📍 Endpoint:', endpoint);
  console.log('📤 Payload:', JSON.stringify(payload, null, 2));
  console.log('⏰ Timestamp:', new Date().toISOString());
  console.groupEnd();
};

/**
 * Log LLM response
 * @param {string} serviceName - Name of the service
 * @param {string} methodName - Name of the method
 * @param {Object} response - Response data
 * @param {number} duration - Request duration in ms
 */
export const logResponse = (serviceName, methodName, response, duration) => {
  if (!LOG_ENABLED) return;

  console.group(`✅ [LLM Response] ${serviceName}.${methodName}`);
  console.log('📥 Response:', JSON.stringify(response, null, 2));
  console.log('⏱️  Duration:', `${duration}ms`);
  console.log('⏰ Timestamp:', new Date().toISOString());
  console.groupEnd();
};

/**
 * Log LLM error
 * @param {string} serviceName - Name of the service
 * @param {string} methodName - Name of the method
 * @param {Error} error - Error object
 * @param {number} duration - Request duration in ms
 */
export const logError = (serviceName, methodName, error, duration) => {
  if (!LOG_ENABLED) return;

  console.group(`❌ [LLM Error] ${serviceName}.${methodName}`);
  console.error('🚨 Error:', error.message);
  console.error('📋 Stack:', error.stack);
  console.log('⏱️  Duration:', `${duration}ms`);
  console.log('⏰ Timestamp:', new Date().toISOString());
  console.groupEnd();
};

/**
 * Wrapper function to add logging to any LLM API call
 * @param {string} serviceName - Name of the service
 * @param {string} methodName - Name of the method
 * @param {string} endpoint - API endpoint
 * @param {Function} apiCall - Async function that makes the API call
 * @returns {Promise} - Result of the API call
 */
export const withLogging = async (serviceName, methodName, endpoint, payload, apiCall) => {
  const startTime = performance.now();

  try {
    logRequest(serviceName, methodName, endpoint, payload);
    const result = await apiCall();
    const duration = Math.round(performance.now() - startTime);
    logResponse(serviceName, methodName, result, duration);
    return result;
  } catch (error) {
    const duration = Math.round(performance.now() - startTime);
    logError(serviceName, methodName, error, duration);
    throw error;
  }
};
